import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import EventScorekeepingFeedItem, {
  Card,
  EventScorekeepingFeedItemFragment,
} from '@/components/Event/EventScorekeeping/EventScorekeepingFeed/EventScorekeepingFeedItem';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql, readFragment } from '@/gql/graphql';

const GetEventScorekeepingDelete = graphql(/* GraphQL */ `
  query GetEventScorekeepingDelete($badgeAssigneeId: uuid!) {
    badge_assignee(
      where: { parent_id: { _eq: $badgeAssigneeId } }
      order_by: { order: asc_nulls_first }
    ) {
      id
      ...EventScorekeepingFeedItem
    }
  }
`, [EventScorekeepingFeedItemFragment]);

const DeleteAssignments = graphql(/* GraphQL */ `
  mutation DeleteAssignments($ids: [uuid!]!) {
    delete_badge_assignee(where: { id: { _in: $ids } }) {
      affected_rows
    }
  }
`);

export default function EventScorekeepingDelete() {
  const { badgeAssigneeId } = useLocalSearchParams<{
    badgeAssigneeId?: string;
  }>();

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetEventScorekeepingDelete,
    variables: {
      badgeAssigneeId: badgeAssigneeId!,
    },
  });

  const { dismiss } = useRouter();

  const [, deleteAssignments] = useMutation(DeleteAssignments);
  const assigneeFragments = data?.badge_assignee || [];

  const onPressDelete = async () => {
    const deletedAssigneeIds = assigneeFragments.map((recipientFragment) => {
      const recipient = readFragment(
        EventScorekeepingFeedItemFragment,
        recipientFragment,
      );

      return recipient.id;
    });

    if (!deletedAssigneeIds.length) return { success: false };

    const result = await deleteAssignments({ ids: deletedAssigneeIds });
    if (result.error || !result.data?.delete_badge_assignee?.affected_rows)
      return { success: false };
    return { success: true };
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Delete Assignments' }} />
      <ContentBoundary
        data={assigneeFragments.length ? assigneeFragments : null}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="No assignments available." />}
      >
        {(assigneeFragments) => (
          <DestructiveAction
            key={badgeAssigneeId}
            label="Delete Assignments"
            submittingLabel="Deleting…"
            onAction={onPressDelete}
            onSuccess={dismiss}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete these assignments?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone.
            </DestructiveActionDescription>
            <Card>
              <EventScorekeepingFeedItem fragment={assigneeFragments} />
            </Card>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
